import { humanize } from '@/lib/format';
import { Keyboard, LogOut, Monitor, Moon, Rows3, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { indexDivisions, useDivisions } from '@/features/divisions/api';
import { useAuth, useUser } from '@/lib/auth';
import { usePref } from '@/lib/prefs';
import { useTheme, type ThemePreference } from '@/lib/theme';

const THEMES: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

function useZoneName(zoneId: string | null): string | null {
  const { data } = useDivisions();
  if (!zoneId) return null;
  return indexDivisions(data).zones.get(zoneId)?.nameEn ?? null;
}

export function UserMenu() {
  const user = useUser();
  const { logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [density, setDensity] = usePref('density');
  const zoneName = useZoneName(user.zoneId);
  const initials = user.name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-md px-1.5 py-1 text-left outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
        <span className="grid size-7 place-content-center rounded-full bg-primary text-2xs font-semibold text-primary-foreground">
          {initials}
        </span>
        <span className="hidden leading-tight sm:block">
          <span className="block max-w-40 truncate text-ui font-medium">{user.name}</span>
          <span className="block text-2xs text-muted-foreground">
            {humanize(user.role)}
            {zoneName && ` · ${zoneName}`}
          </span>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <DropdownMenuLabel className="grid gap-1 text-foreground">
          <span className="truncate text-sm font-medium">{user.name}</span>
          <span className="truncate text-xs text-muted-foreground">{user.email}</span>
          <span className="flex flex-wrap gap-1 pt-0.5">
            <Badge>{humanize(user.role)}</Badge>
            {zoneName && <Badge variant="secondary">{zoneName}</Badge>}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        {THEMES.map(({ value, label, icon: Icon }) => (
          <DropdownMenuItem
            key={value}
            onSelect={() => setTheme(value)}
            className={theme === value ? 'font-semibold' : undefined}
          >
            <Icon /> {label}
            {theme === value && <span className="ml-auto text-xs text-primary">✓</span>}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}
        >
          <Rows3 /> Table rows
          <span className="ml-auto text-xs text-muted-foreground">
            {density === 'compact' ? 'Compact' : 'Comfortable'}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            window.dispatchEvent(
              new KeyboardEvent('keydown', { key: '?', shiftKey: true, bubbles: true }),
            )
          }
        >
          <Keyboard /> Keyboard shortcuts
          <kbd className="ml-auto">?</kbd>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          onSelect={() => {
            logout().catch(() =>
              toast.error('Could not reach the server; you have been signed out locally.'),
            );
          }}
        >
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
